import { useNavigate } from "react-router-dom";
import { BookOpen, ClipboardList, CheckCircle2, Clock } from "lucide-react";

import {
  useGetMyAssessmentDietsQuery,
  MyAssessmentDiet,
} from "../api/logbookAssessmentDietApi";

function StatusPill({ status }: { status: string }) {
  const s = String(status || "").toLowerCase();
  const cls =
    s === "ongoing"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : s === "closed"
      ? "bg-gray-100 text-gray-600 border-gray-200"
      : "bg-amber-50 text-amber-700 border-amber-200";
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
      {status || "pending"}
    </span>
  );
}

export default function AssessorAssessmentDietsPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetMyAssessmentDietsQuery();
  const diets: MyAssessmentDiet[] = Array.isArray(data) ? data : [];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-indigo-900 flex items-center gap-2">
              <ClipboardList className="text-indigo-600" size={26} />
              Logbook Assessment
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Assessment diets you've been assigned to. Open one to grade the
              logbooks handed to you.
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="text-xs font-semibold border px-3 py-1.5 rounded-lg bg-white hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>

        {isLoading && <p className="text-sm text-gray-500">Loading your assignments…</p>}

        {isError && (
          <div className="bg-red-50 border border-red-100 text-red-700 text-sm p-4 rounded-xl">
            Failed to load your assessment assignments.
          </div>
        )}

        {!isLoading && !isError && diets.length === 0 && (
          <div className="bg-white border rounded-xl p-10 text-center">
            <BookOpen className="mx-auto text-gray-300" size={40} />
            <p className="text-sm text-gray-600 mt-3 font-semibold">
              You have no assessment assignments yet.
            </p>
            <p className="text-xs text-gray-400 mt-1">
              When an administrator assigns logbooks to you in an assessment
              diet, they'll appear here.
            </p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {diets.map((d: MyAssessmentDiet) => (
            <button
              key={d.id}
              onClick={() => navigate(`/assessor/assessments/${d.id}`)}
              className="text-left bg-white border rounded-2xl p-5 hover:shadow-md hover:border-indigo-200 transition"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-bold text-gray-900 leading-snug">{d.title}</h3>
                <StatusPill status={d.status} />
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-2 text-center">
                  <div className="text-lg font-black text-indigo-700">
                    {d.my_total_logbooks}
                  </div>
                  <div className="text-[10px] font-semibold text-indigo-600 uppercase">
                    Assigned
                  </div>
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-100 p-2 text-center">
                  <div className="text-lg font-black text-amber-700 flex items-center justify-center gap-1">
                    <Clock size={14} />
                    {d.my_pending_logbooks}
                  </div>
                  <div className="text-[10px] font-semibold text-amber-600 uppercase">
                    Pending
                  </div>
                </div>
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-2 text-center">
                  <div className="text-lg font-black text-emerald-700 flex items-center justify-center gap-1">
                    <CheckCircle2 size={14} />
                    {d.my_assessed_logbooks}
                  </div>
                  <div className="text-[10px] font-semibold text-emerald-600 uppercase">
                    Assessed
                  </div>
                </div>
              </div>

              <div className="mt-4 text-xs font-semibold text-indigo-600">
                Open &amp; grade →
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
